/* Is this search a place? - the Cars search bar's answer to "Gollapudi".
 *
 * The bar searches the cars themselves: model, brand, driver, fuel, seats. A customer
 * who types an area is asking a different question - "what is near there?" - and got
 * "No cars found", which was false: Gollapudi is a few kilometres from half the fleet.
 *
 * So when the words match no car, they are asked of Google Places, near the cars we
 * list. A result is only believed when it is plausibly what was typed AND it lies
 * within reach of our supply; anything else stays "no cars found", which is then true.
 *
 * Uses PlacesService.findPlaceFromQuery, the same call the location picker already
 * makes - no new API for this project. Answers are cached for the session, so a
 * customer retyping or going back and forth pays for one lookup, not one per visit. */

import { soundKey, sameSound, INDIC } from "./soundKey";
import { canonicalCity } from "./cities";

const CACHE_KEY = "sramika_place_lookups";
const MAX_KM_FROM_SUPPLY = 40;

const memo = new Map();
try {
  const saved = JSON.parse(sessionStorage.getItem(CACHE_KEY) || "{}");
  Object.entries(saved).forEach(([k, v]) => memo.set(k, v));
} catch { /* private mode or bad data - start empty */ }

function remember(key, value) {
  memo.set(key, value);
  try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(Object.fromEntries([...memo].slice(-60)))); } catch { /* full */ }
}

/* Words about the car, the driver or the search itself. Stripped before asking the
   map, so "cabs near gollapudi" asks about Gollapudi, and a search made only of
   these ("innova diesel") is never mistaken for a place. */
const CAR_WORDS = new Set((
  "car cars cab cabs taxi taxis vehicle driver drivers near nearby in at around from to the a and with for me " +
  "innova crysta ertiga dzire swift etios xylo scorpio xuv bolero creta amaze verna ciaz aura city " +
  "sedan suv muv hatchback seater seat seats diesel petrol cng electric ev automatic manual ac non " +
  "toyota maruti suzuki hyundai mahindra honda kia tata luxury tempo traveller " +
  "telugu hindi english tamil kannada urdu young old experience experienced available " +
  "wedding outstation local trip round one way"
).split(/\s+/));

/** The words of a search that could be a place name, or "" when there are none. */
export function placeWordsOf(search) {
  return String(search || "").toLowerCase().split(/[\s,]+/)
    .filter((w) => w && !CAR_WORDS.has(w) && !/^\d+[a-z]*$/.test(w))
    .join(" ");
}

export function kmBetween(a, b) {
  const R = 6371, rad = (d) => (d * Math.PI) / 180;
  const dLat = rad(b.lat - a.lat), dLng = rad(b.lng - a.lng);
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(rad(a.lat)) * Math.cos(rad(b.lat)) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// The letters of a name - English, and the Indian scripts from Devanagari to Malayalam
// (only Telugu was kept before, so a search in Hindi, Tamil or Kannada was read as empty).
const letters = (s) => String(s || "").toLowerCase().replace(/[^a-z0-9\u0900-\u0d7f]/g, "");

/* How alike two names are, 0..1 - the Dice coefficient over letter pairs. Tolerates
   the spellings people actually type ("golapudi", "gollapudi") while refusing a
   result that merely shares a city with the query. */
export function nameSimilarity(a, b) {
  const x = letters(a), y = letters(b);
  if (!x || !y) return 0;
  if (x === y) return 1;
  if (x.length < 2 || y.length < 2) return 0;
  const pairs = (s) => { const m = new Map(); for (let i = 0; i < s.length - 1; i++) { const p = s.slice(i, i + 2); m.set(p, (m.get(p) || 0) + 1); } return m; };
  const px = pairs(x), py = pairs(y);
  let hit = 0;
  px.forEach((n, p) => { hit += Math.min(n, py.get(p) || 0); });
  return (2 * hit) / (x.length - 1 + y.length - 1);
}

/* Whether a Places answer is the thing that was typed. Either the names are close, or
   one contains the other ("railway station" -> "Vijayawada Railway Station"). */
export function namesAgree(query, placeName) {
  const q = letters(query), n = letters(placeName);
  if (!q || !n) return false;
  if (n.includes(q) || (q.length >= 5 && q.includes(n))) return true;
  if (nameSimilarity(query, placeName) >= 0.5) return true;
  /* Typed in one script, answered in another - "గొల్లపూడి" and "Gollapudi". Compared by
     how they sound (utils/soundKey), and only with three sounds or more, so a short
     word cannot agree with a place by accident. */
  if (INDIC.test(query) !== INDIC.test(placeName)) {
    const a = soundKey(query), b = soundKey(placeName);
    if (a.replace(/\s/g, "").length >= 3 && (sameSound(a, b) || b.split(" ").some((w, i, all) => sameSound(a, all.slice(i, i + a.split(" ").length).join(" "))))) return true;
  }
  return false;
}

/** The middle of our supply: the average of the listed cars' coordinates. */
export function supplyCentre(cars = []) {
  const pts = cars
    .map((c) => ({ lat: Number(c.addressLat ?? c.ownerDriver?.addressLat), lng: Number(c.addressLng ?? c.ownerDriver?.addressLng) }))
    .filter((p) => Number.isFinite(p.lat) && Number.isFinite(p.lng));
  if (!pts.length) return null;
  return { lat: pts.reduce((s, p) => s + p.lat, 0) / pts.length, lng: pts.reduce((s, p) => s + p.lng, 0) / pts.length };
}

/**
 * Resolve `query` to a place near `near` in `city`, or null.
 * Resolves { name, address, lat, lng }. Never rejects.
 */
export function findPlaceNear(query, { city = "", near = null } = {}) {
  const q = String(query || "").trim();
  const key = `${city.toLowerCase()}|${q.toLowerCase()}`;
  if (memo.has(key)) return Promise.resolve(memo.get(key));

  const places = window.google?.maps?.places;
  if (!q || !places?.PlacesService) return Promise.resolve(null);

  return new Promise((resolve) => {
    const done = (v) => { remember(key, v); resolve(v); };
    try {
      const service = new places.PlacesService(document.createElement("div"));
      const req = {
        query: city ? `${q}, ${city}` : q,
        fields: ["name", "formatted_address", "geometry", "types"],
      };
      if (near) req.locationBias = { center: near, radius: MAX_KM_FROM_SUPPLY * 1000 };
      service.findPlaceFromQuery(req, (results, status) => {
        const r = status === places.PlacesServiceStatus.OK ? results?.[0] : null;
        const loc = r?.geometry?.location;
        if (!r || !loc) return done(null);
        const point = { lat: loc.lat(), lng: loc.lng() };

        /* Two reasons to disbelieve an answer. The name is not what was typed - Places
           always returns SOMETHING, and for a car word it is the city itself. Or it is
           outside the reach of the cars we list, however well the name matches: there
           is a Gollapudi in more than one district. */
        if (!namesAgree(q, r.name)) return done(null);
        if (city && (letters(r.name) === letters(city) || canonicalCity(r.name) === city)) return done(null);
        if (near && kmBetween(near, point) > MAX_KM_FROM_SUPPLY) return done(null);

        done({ name: r.name, address: r.formatted_address || r.name, ...point });
      });
    } catch {
      resolve(null); // not cached: a transient failure should be retried next time
    }
  });
}
